/**
 * Observability shim — console + a real DB sink (public.client_errors).
 *
 * Single stable import path (`@shared/lib/observability`) so we can later swap
 * in Sentry/PostHog without touching call sites. `captureError` now also
 * best-effort persists to the `client_errors` table (append-only, admin-read),
 * giving queryable production telemetry with no third-party dependency.
 *
 * Usage:
 *   captureError(err, { route: 'customer/Bookings' });
 *   track('booking_invoice_opened', { bookingId });
 */
import { supabase } from '@shared/supabase/client';

type Tags = Record<string, string | number | boolean | null | undefined>;

interface ObservabilityConfig {
  release?: string;
  environment?: string;
}

let initialized = false;
let cfg: ObservabilityConfig = {};

export function initObservability(opts: ObservabilityConfig = {}) {
  if (initialized) return;
  initialized = true;
  cfg = opts;
  // Future: Sentry.init({ dsn, release, environment, tracesSampleRate });
  // For now just stash a window-global breadcrumb so DevTools shows we ran.
  if (typeof window !== 'undefined') {
    (window as unknown as { __obs?: ObservabilityConfig }).__obs = cfg;
  }
}

export function captureError(err: unknown, tags?: Tags) {
  if (!err) return;
  const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  // eslint-disable-next-line no-console
  console.error('[obs]', msg, tags ?? {}, err);
  persist(msg, tags);
}

// Best-effort append to public.client_errors. Fire-and-forget: telemetry must
// never throw into or slow down the app, and a failed insert must not recurse.
function persist(message: string, tags?: Tags) {
  try {
    const uid =
      typeof window !== 'undefined'
        ? (window as unknown as { __obsUser?: { id?: string } }).__obsUser?.id ?? null
        : null;
    void supabase
      .from('client_errors')
      .insert({
        message: message.slice(0, 2000),
        context: tags ? (tags as Record<string, unknown>) : null,
        url: typeof location !== 'undefined' ? location.href.slice(0, 500) : null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : null,
        release: cfg.release ?? null,
        environment: cfg.environment ?? null,
        user_id: uid,
      })
      .then(undefined, () => { /* swallow — never let telemetry break the app */ });
  } catch {
    /* swallow */
  }
}

export function captureMessage(msg: string, tags?: Tags) {
  // eslint-disable-next-line no-console
  console.warn('[obs]', msg, tags ?? {});
  // Future: Sentry.captureMessage(msg, { tags });
}

export function track(event: string, props?: Tags) {
  if (!initialized) return;
  // eslint-disable-next-line no-console
  console.debug('[track]', event, props ?? {});
  // Future: posthog.capture(event, props);
}

export function setUserContext(user: { id: string; email?: string | null } | null) {
  if (!initialized) return;
  // Future: Sentry.setUser(user); posthog.identify(user?.id);
  if (typeof window !== 'undefined') {
    (window as unknown as { __obsUser?: unknown }).__obsUser = user;
  }
}

export function getConfig(): Readonly<ObservabilityConfig> {
  return cfg;
}
